import React, { useEffect, useRef, useState } from 'react';
import { noAplicaEdad, PREGUNTAS, MENCIONA_ENFERMEDAD, SALUD_ASESOR, siguientePregunta, opcionesComparador, valorRespuesta, nombreProducto, productoMvp } from '../engine/perfil';
import { ComparadorMVP } from './ComparadorMVP';
import { Accesorios } from './Accesorios';
import { perfilAutos, resumenPerfilAutos } from '../engine/matrizAutos';
import { severidad, ordenarPorPreocupacion } from '../engine/matriz';
import { tienePlanes, PLANES } from '../engine/planes';
import { Typing } from './Bubble';
import { IconArrowRight } from './icons';

const PENSANDO_MS = 1500;
// Autos: el panel del asesor recibe el puntaje de la matriz y las condiciones que trae
const extraAutos = (answers) => {
  const pa = perfilAutos(answers);
  return pa.completo ? [['Perfil de riesgo (matriz autos)', resumenPerfilAutos(pa)], ['Condiciones por perfil', `RC ${pa.sus.rc_texto.toLowerCase()} · deducible parcial ${pa.sus.ded_parcial} · ${pa.sus.aceptacion}`]] : [];
};
const perfilDe = (qs, answers, fijos) => [...fijos, ...qs.filter((q) => answers[q.id] != null).map((q) => [q.label, valorRespuesta(q, answers[q.id])]), ...extraAutos(answers)];

// Perfilamiento: una pregunta a la vez; al final, comparador (o solo las preguntas) o paso al asesor.
// chat: preguntas en burbuja y el texto se escribe en la caja del chat (registrarTexto).
export const Perfilador = ({
  ramo, set, onDone, onCmp, onProgress, onCambio, locked, auto = false, autoAnswers = {},
  chat = false, soloPreguntas = false, fijos = [], registrarTexto, registrarPendiente, previas = {}, pre = {}, usted = false,
}) => {
  const qs = PREGUNTAS[set || ramo];
  const [answers, setAnswers] = useState(previas);
  const [hist, setHist] = useState([]);
  const [idx, setIdx] = useState(() => siguientePregunta(set || ramo, previas, 0));
  const [multiSel, setMultiSel] = useState([]);
  const [pila, setPila] = useState([]); // S5: índices respondidos, para volver
  const [corte, setCorte] = useState(null);
  const [texto, setTexto] = useState('');
  const [elegido, setElegido] = useState(null);
  const [pensando, setPensando] = useState(chat);
  const cmpRef = useRef(false);
  const q = idx >= 0 ? qs[idx] : null;
  const preg = (x) => (usted && x.qu ? x.qu : x.q);
  const showCmp = idx === -1 && !corte && !soloPreguntas;
  const cmpBase = showCmp ? opcionesComparador(ramo, answers) : null;
  const cmp = cmpBase && ramo === 'vida' ? { ...cmpBase, ids: ordenarPorPreocupacion(cmpBase.ids, answers) } : cmpBase;
  const pideTexto = Boolean(q && q.tipo !== 'accesorios' && (!q.opts || q.libre));

  // Selección múltiple: arranca con lo sugerido por el momento (p. ej. «Manejo carro seguido» en el caso A)
  useEffect(() => { setMultiSel(q && q.multi ? (pre[q.id] || []) : []); }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps
  const alternar = (o) => setMultiSel((sel) => {
    if (sel.includes(o)) return sel.filter((x) => x !== o);
    if (q.excluye && o === q.excluye) return [o];
    return [...sel.filter((x) => x !== q.excluye), o];
  });

  useEffect(() => {
    if (!pensando) return undefined;
    const t = setTimeout(() => setPensando(false), PENSANDO_MS);
    return () => clearTimeout(t);
  }, [pensando]);

  useEffect(() => {
    if (showCmp && !cmpRef.current) {
      cmpRef.current = true;
      if (onCmp) onCmp();
    }
  }, [showCmp, onCmp]);

  useEffect(() => { if (onCambio && !showCmp) onCambio(); }, [hist.length, pensando]); // eslint-disable-line react-hooks/exhaustive-deps

  const cerrarRef = useRef(null);
  const responder = (v) => {
    if (locked || !q) return;
    if (q.libre && MENCIONA_ENFERMEDAD.test(String(v))) {
      // No se registra lo que dijo: solo se marca el paso al asesor
      setHist((h) => [...h, { q: preg(q), a: v, nota: SALUD_ASESOR, aviso: true }]);
      setTexto('');
      setCorte(SALUD_ASESOR);
      setIdx(-1);
      onDone({ ramo, respuestas: perfilDe(qs, answers, fijos), answers, escalado: SALUD_ASESOR, eleccion: null, opciones: [] });
      return;
    }
    const next = { ...answers, [q.id]: v, ...(q.derivar ? q.derivar(v) : {}) };
    const nota = q.nota ? q.nota(v) : null;
    const c = q.corte ? q.corte(v) : null;
    setAnswers(next);
    setHist((h) => [...h, { q: preg(q), a: valorRespuesta(q, v), nota: c || nota, aviso: Boolean(c) }]);
    setTexto('');
    if (onProgress) onProgress({ ramo, respuestas: perfilDe(qs, next, fijos), answers: next });
    if (c) {
      setCorte(c);
      setIdx(-1);
      onDone({ ramo, respuestas: perfilDe(qs, next, fijos), answers: next, escalado: c, eleccion: null, opciones: [] });
      return;
    }
    const ni = siguientePregunta(set || ramo, next, idx + 1);
    setPila((pl) => [...pl, idx]);
    setIdx(ni);
    if (chat) setPensando(true);
    if (ni === -1 && soloPreguntas) onDone({ ramo, respuestas: perfilDe(qs, next, fijos), answers: next, escalado: null, eleccion: null, opciones: [] });
  };
  cerrarRef.current = responder;
  const volver = () => {
    if (!pila.length || locked || elegido) return;
    const prev = pila[pila.length - 1];
    setPila((pl) => pl.slice(0, -1));
    setAnswers((a) => { const n = { ...a }; delete n[qs[prev].id]; return n; });
    setHist((h) => h.slice(0, -1));
    setMultiSel([]);
    setIdx(prev);
  };

  const parsear = (txt) => {
    if (q.tipo === 'pesos') {
      const n = Number(String(txt).replace(/\D/g, ''));
      return n > 0 ? n : null;
    }
    return String(txt).trim() || null;
  };

  // Caja única del chat: la pregunta de texto se responde abajo
  useEffect(() => {
    if (!registrarTexto) return undefined;
    if (pideTexto && !locked && !pensando) {
      registrarTexto({ ph: q.ph || 'Escríbelo aquí', submit: (txt) => { const v = parsear(txt); if (v != null) cerrarRef.current(v); return v != null; } });
    } else registrarTexto(null);
    return undefined;
  }, [pideTexto, locked, pensando, idx]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (registrarTexto) registrarTexto(null); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // S11 · la pregunta con opciones queda «pendiente» para que el chat entienda respuestas con palabras propias
  useEffect(() => {
    if (!registrarPendiente) return undefined;
    if (q && q.opts && !locked && !pensando && !elegido) {
      registrarPendiente({
        pregunta: preg(q), opciones: q.opts, multi: Boolean(q.multi),
        elegir: (ops) => cerrarRef.current(q.multi ? q.opts.filter((o) => ops.includes(o)).join(', ') : ops[0]),
      });
    } else registrarPendiente(null);
    return undefined;
  }, [idx, locked, pensando, elegido]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (registrarPendiente) registrarPendiente(null); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enviarTexto = (e) => {
    e.preventDefault();
    const v = parsear(texto);
    if (v != null) responder(v);
  };

  const cerrar = (prod, plan) => {
    const nombres = [...cmp.ids].map((id) => nombreProducto(productoMvp(id)));
    setElegido(prod ? prod.id : 'asesor');
    // S2: severidad de riesgo y razón, para el panel del asesor
    const sev = severidad(answers);
    const pa = ramo === 'autos' ? perfilAutos(answers) : null;
    const extra = ramo === 'vida' ? [['Severidad de riesgo', `${sev.etiqueta} · ${sev.total} puntos (${sev.detalle.map(([r, p]) => `${r} +${p}`).join(', ') || 'sin factores'})`]] : [];
    const modo = prod && tienePlanes(prod.id) ? PLANES[prod.id].modo : null;
    onDone({
      ramo,
      respuestas: [...perfilDe(qs, answers, fijos), ...extra],
      answers,
      escalado: null,
      eleccion: prod ? `${nombreProducto(prod)}${plan ? ` · ${plan.label}` : ''}` : 'Prefiere hablar con un asesor',
      opciones: nombres,
      plan: plan ? { ...plan, producto: nombreProducto(prod), cerrado: modo !== 'caja' } : null,
      severidad: ramo === 'vida' ? sev : null,
      perfilAutos: pa,
    });
  };

  // ▶ Auto: primera opción o el ejemplo del campo; en el comparador, la primera columna
  useEffect(() => {
    if (!auto || locked || elegido || corte || pensando) return undefined;
    const t = setTimeout(() => {
      if (q) {
        if (q.tipo === 'accesorios') responder('Rines de lujo $15.000.000 · total $15.000.000');
        else if (q.multi) responder(autoAnswers[q.id] || (pre[q.id] || [q.opts[0]]).join(', '));
        else if (q.opts) responder(autoAnswers[q.id] || q.opts[0]);
        else responder(q.tipo === 'pesos' ? Number(q.ph.replace(/\D/g, '')) : q.ph.replace(/^Ej\.:\s*/, ''));
      } else if (showCmp) {
        const p0 = productoMvp(cmp.ids.find((id) => !noAplicaEdad(productoMvp(id), answers)) || cmp.ids[0]);
        const pl = tienePlanes(p0.id) ? PLANES[p0.id] : null;
        const plan0 = pl && pl.modo === 'lista' ? pl.planes[0] : null;
        if (pl && !plan0) cerrar(null); else cerrar(p0, plan0);
      }
    }, 1100);
    return () => clearTimeout(t);
  });

  const fmtInput = (v) => {
    const d = v.replace(/\D/g, '');
    return d ? Number(d).toLocaleString('es-CO') : '';
  };

  const Bot = ({ children, tid }) => (chat
    ? <div className="bubble-row bot" data-testid={tid}><div className="bubble bot">{children}</div></div>
    : <div className="pf-bot" data-testid={tid}>{children}</div>);
  const User = ({ children, tid }) => (chat
    ? <div className="bubble-row user" data-testid={tid}><div className="bubble user">{children}</div></div>
    : <div className="pf-user" data-testid={tid}>{children}</div>);

  return (
    <div className={`pf ${chat ? 'chat' : ''}`} data-testid={`perfil-${ramo}`}>
      {hist.map((h, i) => (
        <React.Fragment key={i}>
          <Bot>{h.q}</Bot>
          <User tid={`perfil-resp-${i}`}>{h.a}</User>
          {h.nota ? <div className={`pf-nota ${h.aviso ? 'aviso' : ''}`} data-testid={h.aviso ? 'perfil-corte' : `perfil-nota-${i}`}>{h.nota}</div> : null}
        </React.Fragment>
      ))}
      {q && !locked && pensando ? <Typing /> : null}
      {q && !locked && !pensando ? (
        <>
          <Bot tid="perfil-pregunta">{preg(q)}</Bot>
          {q.tipo === 'accesorios' ? <Accesorios onListo={(v) => responder(v)} locked={locked} base={answers.valor} /> : null}
          {q.multi ? (
            <>
              <div className="pf-opts multi">
                {q.opts.map((o, i) => (
                  <button key={o} type="button" className={multiSel.includes(o) ? 'on' : ''} aria-pressed={multiSel.includes(o)} onClick={() => alternar(o)} data-testid={`perfil-opt-${i}`}>{o}</button>
                ))}
              </div>
              <button type="button" className="pf-continuar" disabled={!multiSel.length} onClick={() => responder(q.opts.filter((o) => multiSel.includes(o)).join(', '))} data-testid="perfil-continuar">
                {`Continuar${multiSel.length ? ` (${multiSel.length})` : ''}`}
              </button>
            </>
          ) : q.opts ? (
            <div className="pf-opts">
              {q.opts.map((o, i) => (
                <button key={o} type="button" onClick={() => responder(o)} data-testid={`perfil-opt-${i}`}>{o}</button>
              ))}
            </div>
          ) : null}
          {!pideTexto || registrarTexto ? null : (
            <form className="pf-form" onSubmit={enviarTexto}>
              <input
                value={q.tipo === 'pesos' ? fmtInput(texto) : texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder={q.ph || 'O escríbalo con sus palabras'}
                inputMode={q.tipo === 'pesos' ? 'numeric' : 'text'}
                data-testid="perfil-input"
              />
              <button type="submit" disabled={!texto.trim()} data-testid="perfil-enviar" aria-label="Enviar respuesta">
                <IconArrowRight size={16} />
              </button>
            </form>
          )}
        </>
      ) : null}
      {pila.length && !locked && !elegido && !pensando && !corte ? (
        <button type="button" className="btn-atras" onClick={volver} data-testid="perfil-atras">← Cambiar la respuesta anterior</button>
      ) : null}
      {showCmp ? (
        <ComparadorMVP
          ids={cmp.ids}
          extra={cmp.extra}
          suyo={cmp.suyo}
          answers={answers}
          locked={locked || Boolean(elegido)}
          elegido={elegido}
          onElegir={(p, plan) => cerrar(p, plan)}
          onAsesor={() => cerrar(null)}
        />
      ) : null}
    </div>
  );
};
